<!-- =========================================================================================
  File Name: CustomerDebtListFormSidebar.vue
  Description: Add New Data - Sidebar component
========================================================================================== -->
<i18n src="@/locales/modules/CustomerTranslate.json"></i18n>

<template>
	<el-dialog
		append-to-body
		destroy-on-close
		:before-close="
			setDialogHide =>
				handleCloseDialog(() => {
					initValues();
					setDialogHide();
				}, isFormDirty)
		"
		class="custom-dialog todo-list-form-sidebar"
		:visible.sync="isSidebarActiveLocal"
	>
		<div slot="title">
			<h4 class="uppercase font-bold block text-xl w-full">{{ data && data.id ? $t("EditProduct") : $t("CreateProduct") }}</h4>
			<p class="text-base text-black font-medium">{{ data ? data.name : "" }}</p>
		</div>
		<perfect-scrollbar ref="vueScrollBar" class="scroll-area--data-list" :settings="settings">
			<div class="p-6">
				<!-- ProductCode -->
				<div class="manufacture-year">
					<label for class="vs-input--label">{{ $t("ProductCode") }}<span class="text-danger">*</span>:</label>
					<cbs-input
						v-model="productCode"
						class="w-full"
						name="productCode"
						:placeholder="`${$t('Actions.Input')} ${$t('ProductCode')}`"
						v-validate="'required'"
						:data-vv-as="$t('ProductCode')"
					/>
					<span class="text-danger text-sm break-normal" v-show="errors.has('productCode')">{{ errors.first("productCode") }}</span>
				</div>
				<!-- ProductName -->
				<div class="manufacture-year mt-5">
					<label for class="vs-input--label">{{ $t("ProductName") }}<span class="text-danger">*</span>:</label>
					<cbs-input
						v-model="productName"
						class="w-full"
						name="productName"
						:placeholder="`${$t('Actions.Input')} ${$t('ProductName')}`"
						v-validate="'required'"
						:data-vv-as="$t('ProductName')"
					/>
					<span class="text-danger text-sm break-normal" v-show="errors.has('productName')">{{ errors.first("productName") }}</span>
				</div>

				<!-- Price -->
				<div class="mt-5">
					<label for class="vs-input--label">{{ $t("Price") }}<span class="text-danger">*</span>:</label>
					<vx-input-group prependClasses="border border-solid border-grey border-r-0">
						<template slot="prepend">
							<div class="prepend-text text-black">
								<span>VNĐ</span>
							</div>
						</template>
						<cbs-masked-input
							dataType="int"
							class="vs-inputx vs-input--input normal w-full"
							v-model.number="price"
							name="price"
							:userSetting="userSettings"
							v-validate="{ min_value: 0, required: true }"
							:data-vv-as="$t('Price')"
							:placeholder="`${$t('Actions.InputMoney')}`"
						/>
					</vx-input-group>
					<span class="text-danger text-sm break-normal" v-show="errors.has('price')">{{ errors.first("price") }}</span>
				</div>
				<!-- Exp -->
				<div class="deadline my-5">
					<label for class="vs-input--label">{{ $t("ExpiredDays") }}:</label>

					<vx-input-group>
						<cbs-masked-input
							dataType="int"
							class="vs-inputx vs-input--input normal w-full"
							v-model.number="expiredDays"
							name="exp-date"
							:userSetting="userSettings"
							v-validate="{ min_value: 0 }"
							:data-vv-as="$t('ExpiredDays')"
							:placeholder="`${$t('ExpiredDays')}`"
						/>
					</vx-input-group>
					<span class="text-danger text-sm break-normal" v-show="errors.has('exp-date')">{{ errors.first("exp-date") }}</span>
				</div>
				<!-- Qty -->
				<div class="mt-5">
					<label for class="vs-input--label">{{ $t("Quantity") }}:</label>
					<vx-input-group prependClasses="border border-solid border-grey border-r-0">
						<cbs-masked-input
							dataType="int"
							class="vs-inputx vs-input--input normal w-full"
							v-model.number="quantity"
							name="quantity"
							:userSetting="userSettings"
							v-validate="{ min_value: 0 }"
							:data-vv-as="$t('Quantity')"
							:placeholder="`${$t('Quantity')}`"
						/>
					</vx-input-group>
					<span class="text-danger text-sm break-normal" v-show="errors.has('quantity')">{{ errors.first("quantity") }}</span>
				</div>
				<!-- QtyLimit -->
				<div class="mt-5">
					<label for class="vs-input--label">{{ $t("QtyLimit") }}:</label>
					<vx-input-group prependClasses="border border-solid border-grey border-r-0">
						<cbs-masked-input
							dataType="int"
							class="vs-inputx vs-input--input normal w-full"
							v-model.number="qtyLimit"
							name="qtyLimit"
							:userSetting="userSettings"
							v-validate="{ min_value: 0 }"
							:data-vv-as="$t('QtyLimit')"
							:placeholder="`${$t('QtyLimit')}`"
						/>
					</vx-input-group>
					<span class="text-danger text-sm break-normal" v-show="errors.has('qtyLimit')">{{ errors.first("qtyLimit") }}</span>
				</div>
				<!-- DESCRIPTION -->
				<div class="description my-5">
					<label for class="vs-input--label">{{ $t("Description") }}:</label>
					<cbs-editor class="normal-placeholder" :placeholder="`${$t('Placeholder.InputDescription')}`" v-model="description" />
				</div>
				<!-- Attach file -->
				<div class="mt-5 mb-3">
					<label for class="vs-input--label">{{ $t("Image") }}:</label>
					<cbs-upload-ref :limit="1" accept=".png" v-model="attachs" />
				</div>
			</div>
		</perfect-scrollbar>

		<div class="flex justify-center flex-wrap items-center" slot="footer">
			<vs-button :type="'border'" class="w-full sm:w-1/3 mb-3 sm:mb-0 sm:mr-6 font-bold" @click.stop="submitData(false)">
				{{ $t("Actions.SaveAndRecreate") }}
			</vs-button>
			<vs-button class="w-full sm:w-1/3 font-bold" @click.stop="submitData(true)">{{ $t("Actions.Save") }}</vs-button>
		</div>
	</el-dialog>
</template>

<script>
import _ from "lodash";
import vSelect from "vue-select";
import CbsEditor from "@/components/cbs-editor/CbsEditor";
import CbsUploadRef from "@/components/cbs-upload-ref/CbsUploadRef";

export default {
	name: "ProductFormSideBar",
	provide() {
		return { parentValidator: this.$validator };
	},
	props: {
		isSidebarActive: {
			type: Boolean,
			required: true
		},
		data: {
			type: Object,
			default: () => {}
		}
	},
	components: {
		vSelect,
		CbsEditor,
		CbsUploadRef
	},
	data() {
		return {
			productCode: "",
			productId: "",
			productName: "",
			price: "",
			description: "",
			attachs: [],
			isFormDirty: false,
			qtyLimit: null,
			quantity: null,
			expiredDays: null,

			settings: {
				// perfectscrollbar settings
				maxScrollbarLength: 60,
				wheelSpeed: 0.6
			}
		};
	},
	watch: {
		isSidebarActive(val) {
			if (!val) return;
			if (Object.entries(this.data).length === 0) {
				this.initValues();
			} else {
				console.log("Edit data entries", this.data);
				let { id, name, code, price, description, front_image, expired_days, quantity, qty_limit } = this.data;
				this.productCode = code;
				this.productId = id;
				this.productName = name;
				this.price = price;
				this.description = description;
				this.attachs = front_image ? [{ ...front_image, extension: "png" }] : [];
				this.qtyLimit = qty_limit || null;
				this.quantity = quantity || null;
				this.expiredDays = expired_days || null;
			}

			this.isFormDirty = false;
			this.initFormData = {
				productCode: this.productCode,
				productId: this.productId,
				productName: this.productName,
				price: this.price,
				description: this.description,
				attachs: this.attachs,
				qtyLimit: this.qtyLimit,
				quantity: this.quantity,
				expiredDays: this.expiredDays
			};
		},
		data: function(newVal, oldVal) {
			if (_.isEmpty(newVal) || newVal.id !== oldVal.id) {
				this.$nextTick(() => {
					if (this.$refs.vueScrollBar) {
						this.$refs.vueScrollBar.$el.scrollTop = 0;
					}
				});
			}
		},
		computedFormData: {
			handler: function(newVal) {
				this.isFormDirty = JSON.stringify(newVal) !== JSON.stringify(this.initFormData);
			},
			deep: true
		}
	},

	computed: {
		isSidebarActiveLocal: {
			get() {
				return this.isSidebarActive;
			},
			set(val) {
				if (!val) {
					this.$emit("closeSidebar");
				}
			}
		},
		computedFormData() {
			return {
				productCode: this.productCode,
				productId: this.productId,
				productName: this.productName,
				price: this.price,
				description: this.description,
				attachs: this.attachs,
				qtyLimit: this.qtyLimit,
				quantity: this.quantity,
				expiredDays: this.expiredDays
			};
		}
	},
	methods: {
		initValues() {
			this.productCode = "";
			this.productId = "";
			this.productName = "";
			this.price = "";
			this.description = "";
			this.attachs = [];
			this.qtyLimit = null;
			this.quantity = null;
			this.expiredDays = null;
			this.isFormDirty = false;
			this.setInitFormData();
			this.$validator.reset();
		},
		setInitFormData() {
			this.$nextTick(() => {
				this.initFormData = {
					productCode: this.productCode,
					productId: this.productId,
					productName: this.productName,
					price: this.price,
					description: this.description,
					attachs: this.attachs,
					qtyLimit: this.qtyLimit,
					quantity: this.quantity,
					expiredDays: this.expiredDays
				};
			});
		},
		handleCreateOrUpdateSuccess(isCloseForm) {
			if (isCloseForm) {
				this.$emit("closeSidebar");
			}
			this.initValues();
			this.isFormDirty = false;
			this.$helper.notifySuccess({
				context: this,
				options: {
					text: `${this.data && this.data.id ? this.$t("EditProduct") : this.$t("CreateProduct")} ${this.$t("Successful").toLowerCase()} `
				}
			});
		},
		async handleCreateOrUpdateCustomerDebt(dataObj, isCloseForm) {
			try {
				this.$vs.loading();
				let response;
				try {
					if (dataObj.id) {
						response = await this.$store.dispatch("customerManagement/updateProduct", dataObj);
					} else {
						response = await this.$store.dispatch("customerManagement/addProduct", dataObj);
					}

					if (response && response.id) {
						this.$emit("onCreateOrUpdateSuccess", response.id);
					}
					this.handleCreateOrUpdateSuccess(isCloseForm);
				} catch (error) {
					this.$helper.notifyError({ context: this, error });
					console.error("HH", error);
				}
			} catch (error) {
				this.$helper.notifyError({ context: this, error });
			} finally {
				this.$vs.loading.close();
			}
		},
		submitData(isCloseForm = false) {
			this.$validator.validateAll().then(result => {
				if (result) {
					try {
						const front_image = _.map(this.attachs, "id");
						let dataObj = {
							id: this.productId,
							name: this.productName,
							code: this.productCode,
							price: this.price,
							description: this.description,
							expired_days: this.expiredDays,
							quantity: this.quantity,
							qty_limit: this.qtyLimit
						};
						if (front_image && front_image[0]) {
							dataObj.front_image = front_image[0];
						}
						console.log("SUB", dataObj);

						this.handleCreateOrUpdateCustomerDebt(dataObj, isCloseForm);
					} catch (error) {
						console.error(error);
					}
				}
			});
		},
		triggerClickDatetimePicker() {
			this.$refs.expDatePickerRef.$children[0].pickerVisible = true;
		}
	},
	created() {}
};
</script>
<style lang="scss" scoped></style>
